import React, { useEffect, useState } from "react";
import { columns } from "./constants";
import { DataGrid } from "@mui/x-data-grid";
import { CustomToolbar } from "./customToolbar";
import { getPaginationText, hasLoading } from "../../business/helpers/util";
import { CustomPagination } from "../../components/pagination";
import { getConfirmationCallList } from "../../business/redux/reducers/callReducer/actions";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import moment from "moment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import "./style.css";
import { ConfirmationCallGraphs } from "./confirmationCallGraphs";

export const ConfirmationCalls = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((s) => s);

  // Add sortModel state
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

  const [filterParams, setFilterParams] = useState({
    accountManagers: [],
    jobDateFrom: moment().format("YYYY-MM-DD"),
    jobDateTo: moment().add(7, "days").format("YYYY-MM-DD"),
  });

  // Add state for filtering unconfirmed jobs
  const [showUnconfirmedOnly, setShowUnconfirmedOnly] = useState(false);

  useEffect(() => {
    document.title = "Confirmation Calls";
  }, []);

  const {
    confirmationCallList,
    confirmationCallListMeta,
    accountManagers,
    branches,
    confirmationCallStatistics,
    notCompletedConfirmationCalls,
  } = useSelector((s) => s.callReducer);

  const [selectedLeads, setSelectedLeads] = useState({});

  // Update useEffect to include sortModel and send sort_by/order
  useEffect(() => {
    const sortField = sortModel[0]?.field || "name";
    const sortOrder = sortModel[0]?.sort || "asc";
    const apiFilterParams = {
      ...filterParams,
      ...(showUnconfirmedOnly && { show_unconfirmed_only: true }),
    };
    dispatch(
      getConfirmationCallList({
        filterParams: apiFilterParams,
        paginationParams: {
          per_page: 25,
          page: 1, // Always reset to page 1 when toggling filter
          sort_by: sortField,
          order: sortOrder,
        },
      })
    ).then((r) => r);
  }, [dispatch, filterParams, sortModel, showUnconfirmedOnly]);

  // Update pagination handlers to include sort_by/order
  const paginationModelChangeHandler = (e) => {
    if (hasLoading(loading)) return;
    const sortField = sortModel[0]?.field || "name";
    const sortOrder = sortModel[0]?.sort || "asc";
    const apiFilterParams = {
      ...filterParams,
      ...(showUnconfirmedOnly && { show_unconfirmed_only: true }),
    };
    return dispatch(
      getConfirmationCallList({
        paginationParams: {
          per_page: e.pageSize,
          page: 1, // Always reset to page 1 when changing page size
          sort_by: sortField,
          order: sortOrder,
        },
        filterParams: apiFilterParams,
      })
    ).then((r) => r);
  };

  const paginationChangeHandler = (newPage, data) => {
    const sortField = sortModel[0]?.field || "name";
    const sortOrder = sortModel[0]?.sort || "asc";
    const paginationParams = {
      per_page: data?.per_page || 25,
      page: newPage,
      sort_by: sortField,
      order: sortOrder,
    };
    const apiFilterParams = {
      ...filterParams,
      ...(showUnconfirmedOnly && { show_unconfirmed_only: true }),
    };
    dispatch(
      getConfirmationCallList({
        paginationParams,
        filterParams: apiFilterParams,
      })
    ).then((r) => r);
  };

  const confirmationCalls = [
    { label: "Completed", value: Number(confirmationCallStatistics.completed) },
    {
      label: "Not Completed",
      value: Number(confirmationCallStatistics.notCompleted),
    },
  ];

  const sortedAccountManagers = [...accountManagers].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <>
      <CustomToolbar
        accountManagers={sortedAccountManagers}
        branches={branches}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        setShowUnconfirmedOnly={setShowUnconfirmedOnly}
      />
      <Grid container spacing={0} direction="row">
        <Grid item lg={3} sx={{ height: "calc(100vh - 164px)", width: "100%" }}>
          <Box sx={{ width: "100%" }}>
            <ConfirmationCallGraphs
              confirmationCalls={confirmationCalls}
              showUnconfirmedOnly={showUnconfirmedOnly}
              setShowUnconfirmedOnly={setShowUnconfirmedOnly}
            />
          </Box>
        </Grid>
        <Grid
          item
          lg={9}
          sx={{
            width: "100%",
            height: "calc(100vh - 164px)",
            paddingLeft: "0px !important",
          }}
        >
          <DataGrid
            sortingOrder={["asc", "desc"]}
            key={confirmationCallListMeta?.total}
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableMultipleColumnsSorting
            disableMultipleSelection
            disableRowSelectionOnClick
            sortingMode="server"
            filterMode="server"
            sx={{
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              ".MuiDataGrid-iconButtonContainer": {
                visibility: "visible",
                "& button": { padding: "0px !important" },
                "& .MuiSvgIcon-root": { color: "#fff" },
              },
              ".MuiDataGrid-sortIcon": { opacity: "inherit !important" },
            }}
            className="custom_table"
            rows={confirmationCallList}
            columns={columns(sortModel, setSortModel)}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model.slice(0, 1))}
            slots={{
              pagination: CustomPagination,
              columnSortedDescendingIcon: ArrowDropDownIcon,
              columnSortedAscendingIcon: ArrowDropUpIcon,
            }}
            slotProps={{
              pagination: {
                data: confirmationCallListMeta,
                onChange: paginationChangeHandler,
              },
            }}
            onPaginationModelChange={paginationModelChangeHandler}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            rowCount={confirmationCallListMeta?.total || 1}
            getRowClassName={(p) => {
              const baseClass =
                p.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";

              // Check if this job is not completed
              const currentJobNumber = String(p.row.Job_Number);
              const isNotCompleted = notCompletedConfirmationCalls.some(
                (notCompletedJob) => {
                  const notCompletedJobNumber = String(
                    notCompletedJob.Job_Number ||
                      notCompletedJob.job_number ||
                      notCompletedJob.jobNumber ||
                      notCompletedJob.id
                  );
                  return notCompletedJobNumber === currentJobNumber;
                }
              );

              // Highlight not completed jobs in red when showing all jobs
              if (!showUnconfirmedOnly && isNotCompleted) {
                return `${baseClass} not-completed-highlight`;
              }

              // Only highlight when filtering for unconfirmed jobs
              const isUnconfirmed = showUnconfirmedOnly;

              return isUnconfirmed
                ? `${baseClass} unconfirmed-highlight`
                : baseClass;
            }}
            loading={loading.tableLoading}
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: `Results per page /`,
                labelDisplayedRows: getPaginationText(
                  confirmationCallListMeta?.per_page || 25,
                  "calls"
                ),
              },
            }}
            rowSelectionModel={
              selectedLeads[`page${confirmationCallListMeta?.current_page}`]
            }
            onRowSelectionModelChange={(e) =>
              setSelectedLeads((prev) => ({
                ...prev,
                [`page${confirmationCallListMeta?.current_page}`]: e,
              }))
            }
          />
        </Grid>
      </Grid>
    </>
  );
};
